import { Box } from '@mui/material';
import { ONBOARDING_STEPPER_STEPS } from './OnboardingWizard.types';
import { useOnboardingLens } from './onboardingLens';

interface OnboardingStepperProps {
  activeStep: number;
  stepperCurrent?: number;
  stepperTotal?: number;
  /** Hidden on step 7 (loading / complete / error). */
  showStepper?: boolean;
  /** Figma welcome footer stepper (132×12, 8px gap). */
  useWelcomeMetrics?: boolean;
}

/** Figma 16692:5525 — 7-dot progress indicator (Welcome footer layout, all steps). */
export function OnboardingStepper({
  activeStep,
  stepperCurrent,
  stepperTotal,
  showStepper = true,
  useWelcomeMetrics = false,
}: OnboardingStepperProps) {
  const { layout, typeDefault, divider } = useOnboardingLens();
  const welcomeStepper = layout.welcomeStep1.stepper;

  if (!showStepper) {
    return null;
  }

  const totalSteps = stepperTotal ?? ONBOARDING_STEPPER_STEPS;
  const currentStep =
    stepperCurrent ?? Math.min(activeStep, ONBOARDING_STEPPER_STEPS);
  const dotSizePx = useWelcomeMetrics
    ? parseFloat(welcomeStepper.dotSize)
    : parseFloat(layout.footer.dotSize);
  const stepperGapPx = useWelcomeMetrics
    ? parseFloat(welcomeStepper.gap)
    : parseFloat(layout.footer.stepperGap);

  return (
    <Box
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-valuenow={currentStep}
      aria-label={`Step ${currentStep} of ${totalSteps}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: useWelcomeMetrics ? welcomeStepper.gap : layout.footer.stepperGap,
        width: useWelcomeMetrics
          ? welcomeStepper.width
          : totalSteps * dotSizePx + (totalSteps - 1) * stepperGapPx,
        height: useWelcomeMetrics ? welcomeStepper.dotSize : layout.footer.dotSize,
      }}
    >
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;

        return (
          <Box
            key={stepNumber}
            sx={{
              width: useWelcomeMetrics ? welcomeStepper.dotSize : layout.footer.dotSize,
              height: useWelcomeMetrics ? welcomeStepper.dotSize : layout.footer.dotSize,
              borderRadius: '50%',
              boxSizing: 'border-box',
              backgroundColor: isActive ? typeDefault : divider,
              flexShrink: 0,
            }}
          />
        );
      })}
    </Box>
  );
}
